/**
 * PHASE 7 — celestial frame math for the sky renderer (pure, no GL).
 *
 * All coordinates are J2000 unless stated. The rendering pipeline composes
 * three rotations on the CPU and uploads them as uniforms:
 *
 *   stars/lines/DSO:   clip = P * V(qCam) * R[eqd->ENU] * R[precession] * xyz_J2000
 *   skybox texture:    dir_ENU -> dir_eqd -> dir_J2000 -> dir_gal -> [u, v]
 *
 * where the galactic frame is fixed by the standard constants (NGC centre
 * and north galactic pole in J2000), matching the ESA Gaia DR2 equirectangular
 * texture (verified against the image: bright galactic plane runs horizontally
 * through u=0.5, v=0.5).
 *
 * The eqd->ENU matrix is analytic (hour-angle + polar tilt); its correctness
 * is pinned by an agreement test against the astronomy engine.
 */

import type { Quat } from "../orientation/quaternion";
import type { Mat4 } from "./mat4";
import { identity, multiply, rotationFromQuat } from "./mat4";

/**
 * Unit vector (in equatorial xyz) from J2000 RA/Dec. x = vernal equinox,
 * z = celestial pole. For of-date input it represents that frame's pole.
 */
export function equatorialVector(raHours: number, decDeg: number): [number, number, number] {
  const ra = raHours * 15 * (Math.PI / 180);
  const dec = decDeg * (Math.PI / 180);
  return [Math.cos(dec) * Math.cos(ra), Math.cos(dec) * Math.sin(ra), Math.sin(dec)];
}

export function equatorialDegrees(v: readonly [number, number, number]): { raHours: number; decDeg: number } {
  const decRad = Math.asin(clamp1(v[2]));
  const raRad = Math.atan2(v[1], v[0]);
  return { raHours: (((raRad * 180) / Math.PI / 15) % 24 + 24) % 24, decDeg: (decRad * 180) / Math.PI };
}

/**
 * Rotation matrix mapping an of-date equatorial unit vector to the ENU frame
 * (x East, y North, z Up). Built analytically:
 *
 *   E = -sin L * x + cos L * y
 *   N = -sin p * cos L * x - sin p * sin L * y + cos p * z
 *   U =  cos p * cos L * x + cos p * sin L * y + sin p * z
 *
 * with L = local sidereal angle and p = latitude. Verified in tests against
 * the engine's equatorialToHorizontal.
 */
export function buildEqdToEnuMatrix(latitudeDeg: number, localSiderealHours: number): Mat4 {
  const p = latitudeDeg * (Math.PI / 180);
  const l = (localSiderealHours % 24) * 15 * (Math.PI / 180);
  const sinL = Math.sin(l);
  const cosL = Math.cos(l);
  const sinP = Math.sin(p);
  const cosP = Math.cos(p);
  return [
    -sinL, -sinP * cosL, cosP * cosL, 0, // col0: image of x_eq
    cosL, -sinP * sinL, cosP * sinL, 0, // col1: image of y_eq
    0, cosP, sinP, 0, // col2: image of z_eq (pole)
    0, 0, 0, 1,
  ];
}

/**
 * Rotation matrix of the J2000 -> of-date rotation (from the engine), as a
 * Mat4. `rot` is row-major 3x3.
 */
export function buildPrecessionMatrix(rotation: { rot: readonly (readonly number[])[] }): Mat4 {
  const r00 = rotation.rot[0]![0]!;
  const r01 = rotation.rot[0]![1]!;
  const r02 = rotation.rot[0]![2]!;
  const r10 = rotation.rot[1]![0]!;
  const r11 = rotation.rot[1]![1]!;
  const r12 = rotation.rot[1]![2]!;
  const r20 = rotation.rot[2]![0]!;
  const r21 = rotation.rot[2]![1]!;
  const r22 = rotation.rot[2]![2]!;
  return [
    r00, r10, r20, 0, // col0
    r01, r11, r21, 0, // col1
    r02, r12, r22, 0, // col2
    0, 0, 0, 1,
  ];
}

/** Inverse rotation of buildPrecessionMatrix (of-date -> J2000). */
export function inversePrecessionMatrix(rotation: { rot: readonly (readonly number[])[] }): Mat4 {
  return transpose33(buildPrecessionMatrix(rotation));
}

// IAU-defined galactic frame anchors (J2000). The pair is defined to be
// exactly 90 deg apart, so the derived basis is exactly orthonormal:
//   - north galactic pole: RA 12h51m26.282s, Dec +27d07m42.01s
//   - galactic centre:     RA 17h45m37.2s,  Dec -28d56m10.2s
const NGP_RA_HOURS = 12.8573006;
const NGP_DEC_DEG = 27.1283361;
const GC_RA_HOURS = 17.76034;
const GC_DEC_DEG = -28.936175;

/**
 * Matrix mapping equatorial (J2000) xyz to galactic xyz. Built from the two
 * anchor directions (x_gal = galactic centre, z_gal = NGP), y_gal completes
 * the right-handed frame. The anchors are exactly 90 deg apart in the IAU
 * definition; z is re-derived as x x y so the frame is orthonormal to
 * machine precision even with rounded anchor constants.
 */
export function buildEquatorialToGalacticMatrix(): Mat4 {
  const xg = equatorialVector(GC_RA_HOURS, GC_DEC_DEG);
  const zg = equatorialVector(NGP_RA_HOURS, NGP_DEC_DEG);
  const yg = normalize(cross(zg, xg));
  const x = normalize(xg);
  const z = cross(x, yg);
  // Rows of the rotation: galactic x/y/z expressed in equatorial coords.
  return [
    x[0], yg[0], z[0], 0,
    x[1], yg[1], z[1], 0,
    x[2], yg[2], z[2], 0,
    0, 0, 0, 1,
  ];
}

/**
 * The full skybox rotation: ENU -> of-date -> J2000 -> galactic, as a Mat4
 * (safe to upload as mat3). `rotation` is j2000ToOfDateRotation(time).
 */
export function buildEnuToGalacticMatrix(
  latitudeDeg: number,
  localSiderealHours: number,
  rotation: { rot: readonly (readonly number[])[] } | null,
): Mat4 {
  const enuToEqd = transpose33(buildEqdToEnuMatrix(latitudeDeg, localSiderealHours));
  const eqdToJ2000 = rotation ? inversePrecessionMatrix(rotation) : identity();
  const galactic = buildEquatorialToGalacticMatrix();
  return multiply(galactic, multiply(eqdToJ2000, enuToEqd));
}

export interface GalacticDegrees {
  longitudeDeg: number;
  latitudeDeg: number;
}

/** Galactic (l, b) from equatorial J2000 coordinates. */
export function galacticFromEquatorial(raHours: number, decDeg: number): GalacticDegrees {
  const m = buildEquatorialToGalacticMatrix();
  const g = transformDir(m, equatorialVector(raHours, decDeg));
  let longitudeDeg = (Math.atan2(g[1], g[0]) * 180) / Math.PI;
  if (longitudeDeg < 0) longitudeDeg += 360;
  return {
    longitudeDeg,
    latitudeDeg: (Math.asin(clamp1(g[2])) * 180) / Math.PI,
  };
}

/**
 * Equirectangular texture coordinates for the galactic frame texture.
 * u = 0.5 + l/360 (galactic longitude increasing rightward, centre at 0.5),
 * v = (90 - b)/180 (north galactic pole at v = 0, top).
 */
export function uvFromGalactic(galactic: GalacticDegrees): { u: number; v: number } {
  const u = (0.5 + galactic.longitudeDeg / 360 + 1) % 1;
  return { u, v: (90 - galactic.latitudeDeg) / 180 };
}

/** Convenience: full chain J2000 RA/Dec -> texture UV. */
export function uvFromEquatorial(raHours: number, decDeg: number): { u: number; v: number } {
  return uvFromGalactic(galacticFromEquatorial(raHours, decDeg));
}

export function transformDir(m: Mat4, v: readonly [number, number, number]): [number, number, number] {
  return [m[0]! * v[0] + m[4]! * v[1] + m[8]! * v[2], m[1]! * v[0] + m[5]! * v[1] + m[9]! * v[2], m[2]! * v[0] + m[6]! * v[1] + m[10]! * v[2]];
}

/** Upper-left 3x3 transpose (a rotation's inverse is its transpose). */
export function transpose33(m: Mat4): Mat4 {
  const row = (c: number): number => m[c]!;
  return [
    row(0), row(4), row(8), 0,
    row(1), row(5), row(9), 0,
    row(2), row(6), row(10), 0,
    0, 0, 0, 1,
  ];
}

function cross(a: readonly [number, number, number], b: readonly [number, number, number]): [number, number, number] {
  return [a[1]! * b[2]! - a[2]! * b[1]!, a[2]! * b[0]! - a[0]! * b[2]!, a[0]! * b[1]! - a[1]! * b[0]!];
}

function normalize(v: readonly [number, number, number]): [number, number, number] {
  const n = Math.hypot(v[0], v[1], v[2]);
  return [v[0]! / n, v[1]! / n, v[2]! / n];
}

function clamp1(v: number): number {
  return Math.min(1, Math.max(-1, v));
}

/**
 * Build the single star-layer matrix: clip = P * V * R[eqd->ENU] * R[prec].
 * `rotation` is null when no of-date conversion is wanted.
 */
export function buildStarProjection(
  projection: Mat4,
  qCamera: Quat,
  latitudeDeg: number,
  localSiderealHours: number,
  rotation: { rot: readonly (readonly number[])[] } | null,
): Mat4 {
  const eqdToEnu = buildEqdToEnuMatrix(latitudeDeg, localSiderealHours);
  const star = rotation ? multiply(eqdToEnu, buildPrecessionMatrix(rotation)) : eqdToEnu;
  return multiply(projection, multiply(rotationFromQuat(qCamera), star));
}